import React from "react";
import { useInView } from "../../hooks/use-in-view";
import { FlaskConical, Microscope, Truck, ClipboardCheck, Beaker, Factory } from "lucide-react";

const steps = [
  { icon: Factory, title: "Raw Material Intake", desc: "Bulk salts, sulphur and reactants are verified against supplier CoAs and bonded inward." },
  { icon: FlaskConical, title: "Reactor Synthesis", desc: "Acid synthesis in glass-lined / FRP reactors with precise temperature, pressure & dosing control for batch-to-batch consistency." },
  { icon: Beaker, title: "Multi-Stage Purification", desc: "Distillation, filtration and ion-exchange to achieve Commercial / C.P. / L.R. / A.R. grades." },
  { icon: Microscope, title: "In-House QC Testing", desc: "Titration, AAS, spectrophotometry and density checks against IS / IP / BP / USP specs." },
  { icon: ClipboardCheck, title: "Batch Documentation", desc: "Batch records, CoA, MSDS and TDS prepared and digitally signed by QA before release." },
  { icon: Truck, title: "Dispatch & Logistics", desc: "FRP / HDPE drums, IBCs or stainless tankers — dispatched with e-way bill within 24 hrs." }
];

const Process = () => {
  const [ref, inView] = useInView();
  return (
    <section id="process" className="py-20 lg:py-28 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-dot opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-teal-700">Manufacturing Process</div>
          <h2 className="mt-3 font-display font-extrabold text-slate-900 text-[34px] lg:text-[46px] leading-[1.08]">
            From raw salt to <span className="text-gradient-anim">certified tanker</span> in six controlled steps
          </h2>
          <p className="mt-4 text-slate-600 text-[16px] leading-relaxed">
            Every kilogram we ship has passed through a six-stage production and QC pipeline. Transparency, traceability and repeatability are built into the workflow.
          </p>
        </div>

        <div ref={ref} className="mt-14 relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-[2px]">
            <div className="h-full mx-16 bg-gradient-to-r from-teal-200 via-cyan-300 to-teal-200" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-5">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className={`reveal ${inView ? "is-visible" : ""} relative bg-white rounded-2xl border border-slate-200 p-5 card-lift`}
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <div className="flex items-center justify-between">
                    <div className="h-12 w-12 rounded-xl brand-band flex items-center justify-center shadow-md shadow-teal-900/20">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <span className="font-display font-extrabold text-[28px] text-slate-100">0{i + 1}</span>
                  </div>
                  <h3 className="mt-4 font-display font-bold text-[15px] text-slate-900">{s.title}</h3>
                  <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
